import type { ChangeEvent, RefObject } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

type CardAssetsSectionProps = {
  stampUrl: string | null;
  signatureUrl: string | null;
  uploadingStamp: boolean;
  uploadingSignature: boolean;
  stampInputRef: RefObject<HTMLInputElement | null>;
  signatureInputRef: RefObject<HTMLInputElement | null>;
  onStampUpload: (e: ChangeEvent<HTMLInputElement>) => void;
  onSignatureUpload: (e: ChangeEvent<HTMLInputElement>) => void;
};

export function CardAssetsSection({
  stampUrl,
  signatureUrl,
  uploadingStamp,
  uploadingSignature,
  stampInputRef,
  signatureInputRef,
  onStampUpload,
  onSignatureUpload,
}: CardAssetsSectionProps) {
  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <AssetUpload
        label="Library stamp"
        hint="PNG/JPG — transparent background recommended"
        url={stampUrl}
        alt="Stamp"
        uploading={uploadingStamp}
        inputRef={stampInputRef}
        onUpload={onStampUpload}
        actionLabel={stampUrl ? "Replace stamp" : "Upload stamp"}
      />
      <AssetUpload
        label="Authorized signature"
        hint="PNG/JPG of the authorized signatory"
        url={signatureUrl}
        alt="Signature"
        uploading={uploadingSignature}
        inputRef={signatureInputRef}
        onUpload={onSignatureUpload}
        actionLabel={signatureUrl ? "Replace signature" : "Upload signature"}
        imageClassName="max-h-16"
      />
    </div>
  );
}

function AssetUpload({
  label,
  hint,
  url,
  alt,
  uploading,
  inputRef,
  onUpload,
  actionLabel,
  imageClassName = "max-h-20",
}: {
  label: string;
  hint: string;
  url: string | null;
  alt: string;
  uploading: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  onUpload: (e: ChangeEvent<HTMLInputElement>) => void;
  actionLabel: string;
  imageClassName?: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <Label className="text-sm font-medium text-slate-900">{label}</Label>
      <p className="mt-1 text-xs text-slate-500">{hint}</p>
      <div className="mt-3 flex min-h-[88px] items-center justify-center rounded-md border border-dashed border-slate-200 bg-slate-50/80 p-3">
        {url ? (
          <img src={url} alt={alt} className={`${imageClassName} object-contain`} />
        ) : (
          <p className="text-xs text-slate-400">No image uploaded</p>
        )}
      </div>
      <input
        ref={inputRef as RefObject<HTMLInputElement>}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onUpload}
      />
      <Button
        variant="outline"
        size="sm"
        className="mt-3 gap-1.5"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
      >
        <Upload className="h-4 w-4" />
        {uploading ? "Uploading…" : actionLabel}
      </Button>
    </div>
  );
}
